'use client';
import { jsxs, jsx } from 'react/jsx-runtime';
import { twMerge, twJoin } from 'tailwind-merge';
import Typography from '../../Base/Typography/Typography.js';
import Box from '../../Base/Layouts/Box/Box.js';
import '../../Base/Layouts/FullBleed/FullBleed.js';
import Checkbox from '../Checkbox/Checkbox.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const CheckboxListItemContainer = (_param)=>{
    var { children, disabled, hasDescription, className } = _param, rest = _object_without_properties(_param, [
        "children",
        "disabled",
        "hasDescription",
        "className"
    ]);
    return /*#__PURE__*/ jsx("label", _object_spread_props(_object_spread({
        className: twMerge('grid h-min grid-cols-checkbox-list-item-container grid-rows-checkbox-list-item-container gap-x-3 grid-areas-checkbox-list-item-container', hasDescription ? 'gap-y-0.5' : 'gap-y-0', disabled ? 'cursor-not-allowed' : 'cursor-pointer', className)
    }, rest), {
        children: children
    }));
};
/**
 * 체크박스와 라벨, 설명을 포함하는 리스트 아이템 컴포넌트입니다.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.label] - 체크박스 옆에 표시될 라벨 텍스트
 * @param {string} [props.description] - 라벨 아래에 표시될 설명 텍스트
 * @param {string} [props.errorMessage] - 체크박스 오류 메시지
 * @param {string} [props.CheckboxClassName] - 체크박스에 적용될 커스텀 클래스
 * @param {CheckboxType} [props.type='box'] - 체크박스 타입 ('box' | 'circle' | 'sub')
 * @param {boolean} [props.checked] - 체크박스의 선택 상태
 * @param {boolean} [props.disabled=false] - 체크박스의 비활성화 상태
 * @param {boolean} [props.isError=false] - 체크박스의 에러 상태
 * @param {Function} [props.onChange] - 체크박스 상태 변경 시 호출되는 콜백 함수
 * @param {ReactNode} [props.children] - 라벨 영역에 표시될 추가 컴포넌트
 *
 * @example
 * // 기본 사용
 * <CheckboxListItem
 *   label="이용약관 동의"
 *   description="서비스 이용을 위한 필수 약관입니다."
 *   checked={checked}
 *   onChange={handleChange}
 * />
 *
 * // 비활성화 상태
 * <CheckboxListItem
 *   label="선택 불가 항목"
 *   disabled={true}
 * />
 *
 * // 에러 상태
 * <CheckboxListItem
 *   label="필수 선택 항목"
 *   isError={true}
 *   errorMessage="이 항목은 필수로 선택해야 합니다."
 * />
 *
 * // 원형 체크박스
 * <CheckboxListItem
 *   label="원형 체크박스"
 *   type="circle"
 * />
 */ const CheckboxListItem = (_param)=>{
    var { label, description, type = 'box', checked, disabled = false, isError = false, errorMessage, className, CheckboxClassName, onChange, children } = _param, rest = _object_without_properties(_param, [
        "label",
        "description",
        "type",
        "checked",
        "disabled",
        "isError",
        "errorMessage",
        "className",
        "CheckboxClassName",
        "onChange",
        "children"
    ]);
    return /*#__PURE__*/ jsxs(CheckboxListItemContainer, {
        "data-testid": "design-system-checkboxList--item",
        disabled: disabled,
        hasDescription: !!description,
        className: twMerge('group', className),
        children: [
            /*#__PURE__*/ jsx(Box, {
                className: "cursor-pointer pt-1 grid-in-checkbox",
                children: /*#__PURE__*/ jsx(Checkbox, _object_spread({
                    checked: checked,
                    isError: isError,
                    disabled: disabled,
                    onChange: onChange,
                    className: CheckboxClassName,
                    type: type
                }, rest))
            }),
            /*#__PURE__*/ jsxs(Box, {
                className: "flex w-full items-center grid-in-label",
                "data-testid": "design-system-checkboxList--item-label-root",
                children: [
                    label && /*#__PURE__*/ jsx(Typography, {
                        variant: "body1",
                        className: twJoin('w-full whitespace-normal break-words', disabled ? 'text-w-gray-300' : 'text-w-gray-900'),
                        "data-testid": "design-system-checkboxList--item-label",
                        children: label
                    }),
                    children
                ]
            }),
            isError && errorMessage && /*#__PURE__*/ jsx(Box, {
                className: "flex items-center grid-in-error",
                children: /*#__PURE__*/ jsx(Typography, {
                    variant: "body2",
                    className: "text-w-red-500",
                    "data-testid": "design-system-checkboxList--item-error",
                    children: errorMessage
                })
            }),
            description && /*#__PURE__*/ jsx(Box, {
                className: "flex items-center grid-in-description",
                children: /*#__PURE__*/ jsx(Typography, {
                    variant: "body2",
                    className: disabled ? 'text-w-gray-300' : 'text-w-gray-600',
                    "data-testid": "design-system-checkboxList--item-description",
                    children: description
                })
            })
        ]
    });
};

export { CheckboxListItem as default };
